import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Code, Book, Key, AlertTriangle, ArrowRight, Copy, Check } from 'lucide-react';

interface ApiDocsData {
  title: string;
  version: string;
  description: string;
  baseUrl: string;
  authentication: any;
  sections: any[];
  vsCodeIntegration: any;
  errors: any;
  hierarchy: any;
}

export default function ApiDocsPanel({ onClose: _onClose }: { onClose: () => void }) {
  const [docs, setDocs] = useState<ApiDocsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState<string | null>('authentication');
  const [expandedEndpoint, setExpandedEndpoint] = useState<string | null>(null);
  const [copiedText, setCopiedText] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api-docs.json')
      .then((res) => res.json())
      .then((data) => {
        setDocs(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const copyCode = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(id);
    setTimeout(() => setCopiedText(null), 2000);
  };

  const methodColor = (method: string) => {
    switch (method.toUpperCase()) {
      case 'GET': return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
      case 'POST': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'PUT': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
      case 'PATCH': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'DELETE': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-center flex-1">
          <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!docs) {
    return (
      <div className="flex flex-col h-full p-4">
        <p className="text-sm text-gray-500">Failed to load API documentation.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2 mb-1">
          <Book size={16} className="text-accent" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{docs.title}</h2>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500">v{docs.version}</span>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">{docs.description}</p>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {/* Authentication */}
        <CollapsibleSection
          title="Authentication"
          icon={<Key size={14} />}
          expanded={expandedSection === 'authentication'}
          onToggle={() => setExpandedSection(expandedSection === 'authentication' ? null : 'authentication')}
        >
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">{docs.authentication.description}</p>
          {docs.authentication.methods.map((method: any, i: number) => (
            <div key={i} className="mb-3 p-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/50">
              <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 mb-1">{method.type}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">{method.description}</p>
              <CodeBlock text={method.header} id={`auth-${i}`} onCopy={copyCode} copied={copiedText} />
              {method.setup && (
                <div className="mt-2">
                  <p className="text-[10px] font-medium text-gray-600 dark:text-gray-400 mb-1">Setup Steps:</p>
                  <ol className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5 list-decimal list-inside">
                    {method.setup.map((step: string, j: number) => (
                      <li key={j}>{step}</li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          ))}
        </CollapsibleSection>

        {/* API Sections */}
        {docs.sections.map((section: any, si: number) => (
          <CollapsibleSection
            key={si}
            title={section.title}
            icon={<Code size={14} />}
            expanded={expandedSection === section.title}
            onToggle={() => setExpandedSection(expandedSection === section.title ? null : section.title)}
          >
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{section.description}</p>
            <div className="space-y-1">
              {section.endpoints.map((ep: any, ei: number) => {
                const epKey = `${section.title}-${ei}`;
                const isExpanded = expandedEndpoint === epKey;
                return (
                  <div key={ei} className="rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
                    <button
                      onClick={() => setExpandedEndpoint(isExpanded ? null : epKey)}
                      className="w-full flex items-center gap-2 px-2.5 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${methodColor(ep.method)}`}>
                        {ep.method}
                      </span>
                      <code className="text-xs text-gray-700 dark:text-gray-300 flex-1 font-mono">{ep.path}</code>
                      {isExpanded ? <ChevronDown size={12} className="text-gray-400" /> : <ChevronRight size={12} className="text-gray-400" />}
                    </button>
                    {isExpanded && (
                      <div className="px-2.5 pb-2.5 pt-1 border-t border-gray-100 dark:border-gray-600">
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">{ep.description}</p>

                        {ep.permissions && (
                          <div className="flex items-center gap-1 mb-2">
                            <span className="text-[10px] text-gray-400">Permissions:</span>
                            {ep.permissions.map((p: string) => (
                              <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">{p}</span>
                            ))}
                          </div>
                        )}

                        {ep.queryParams && (
                          <ParamTable title="Query Parameters" params={ep.queryParams} />
                        )}
                        {ep.pathParams && (
                          <ParamTable title="Path Parameters" params={ep.pathParams} />
                        )}
                        {ep.body && (
                          <ParamTable title="Request Body" params={ep.body} showRequired />
                        )}
                        {ep.example && (
                          <div className="mt-2">
                            <p className="text-[10px] font-medium text-gray-500 mb-1">Example</p>
                            <CodeBlock
                              text={typeof ep.example.request === 'string' ? ep.example.request : JSON.stringify(ep.example.request, null, 2)}
                              id={`ep-${epKey}`}
                              onCopy={copyCode}
                              copied={copiedText}
                            />
                            {ep.example.response && (
                              <div className="mt-1">
                                <p className="text-[10px] text-gray-400 mb-0.5">Response:</p>
                                <CodeBlock
                                  text={JSON.stringify(ep.example.response, null, 2)}
                                  id={`ep-${epKey}-resp`}
                                  onCopy={copyCode}
                                  copied={copiedText}
                                />
                              </div>
                            )}
                          </div>
                        )}
                        {ep.examples && (
                          <div className="mt-2 space-y-3">
                            {ep.examples.map((ex: any, exIdx: number) => (
                              <div key={exIdx}>
                                {ex.title && <p className="text-[10px] font-medium text-gray-500 mb-1">{ex.title}</p>}
                                <CodeBlock
                                  text={typeof ex.request === 'string' ? ex.request : JSON.stringify(ex.request, null, 2)}
                                  id={`ep-${epKey}-ex${exIdx}`}
                                  onCopy={copyCode}
                                  copied={copiedText}
                                />
                                {ex.notes && <p className="text-[9px] text-gray-400 mt-0.5 italic">{ex.notes}</p>}
                                {ex.response && (
                                  <div className="mt-1">
                                    <p className="text-[10px] text-gray-400 mb-0.5">Response:</p>
                                    <CodeBlock
                                      text={JSON.stringify(ex.response, null, 2)}
                                      id={`ep-${epKey}-ex${exIdx}-resp`}
                                      onCopy={copyCode}
                                      copied={copiedText}
                                    />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        ))}

        {/* VS Code Integration */}
        <CollapsibleSection
          title="VS Code Integration"
          icon={<Code size={14} />}
          expanded={expandedSection === 'vscode'}
          onToggle={() => setExpandedSection(expandedSection === 'vscode' ? null : 'vscode')}
        >
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{docs.vsCodeIntegration.description}</p>
          {docs.vsCodeIntegration.steps.map((step: any, i: number) => (
            <div key={i} className="mb-3">
              <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 mb-1">{step.title}</p>
              <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-pre-wrap">{step.content}</div>
            </div>
          ))}
          {docs.vsCodeIntegration.scriptExample && (
            <div className="mt-3">
              <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 mb-1">
                {docs.vsCodeIntegration.scriptExample.title}
              </p>
              <CodeBlock
                text={docs.vsCodeIntegration.scriptExample.code}
                id="vscode-script"
                onCopy={copyCode}
                copied={copiedText}
              />
            </div>
          )}
        </CollapsibleSection>

        {/* Hierarchy */}
        <CollapsibleSection
          title="Work Item Hierarchy"
          icon={<ArrowRight size={14} />}
          expanded={expandedSection === 'hierarchy'}
          onToggle={() => setExpandedSection(expandedSection === 'hierarchy' ? null : 'hierarchy')}
        >
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{docs.hierarchy.description}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-600">
                  <th className="text-left py-1.5 px-2 text-gray-500 font-medium">Type</th>
                  <th className="text-left py-1.5 px-2 text-gray-500 font-medium">Parent</th>
                  <th className="text-left py-1.5 px-2 text-gray-500 font-medium">Children</th>
                </tr>
              </thead>
              <tbody>
                {docs.hierarchy.rules.map((rule: any, i: number) => (
                  <tr key={i} className="border-b border-gray-100 dark:border-gray-700">
                    <td className="py-1.5 px-2 font-medium text-gray-800 dark:text-gray-200">{rule.type}</td>
                    <td className="py-1.5 px-2 text-gray-500 dark:text-gray-400">{rule.parent}</td>
                    <td className="py-1.5 px-2 text-gray-500 dark:text-gray-400">{rule.children}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CollapsibleSection>

        {/* Errors */}
        <CollapsibleSection
          title="Error Responses"
          icon={<AlertTriangle size={14} />}
          expanded={expandedSection === 'errors'}
          onToggle={() => setExpandedSection(expandedSection === 'errors' ? null : 'errors')}
        >
          <div className="space-y-2">
            {docs.errors.codes.map((err: any, i: number) => (
              <div key={i} className="p-2 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-bold text-red-600 dark:text-red-400">{err.code}</span>
                  <span className="text-xs text-gray-700 dark:text-gray-300">{err.description}</span>
                </div>
                <code className="text-[10px] text-gray-500 dark:text-gray-400 break-all">{err.example}</code>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      </div>
    </div>
  );
}


/* ── Helper Components ───────────────────────────── */

function CollapsibleSection({
  title,
  icon,
  expanded,
  onToggle,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <span className="text-accent">{icon}</span>
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 flex-1">{title}</span>
        {expanded ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-gray-100 dark:border-gray-700">
          {children}
        </div>
      )}
    </div>
  );
}

function CodeBlock({
  text,
  id,
  onCopy,
  copied,
}: {
  text: string;
  id: string;
  onCopy: (text: string, id: string) => void;
  copied: string | null;
}) {
  return (
    <div className="relative group">
      <pre className="text-[11px] bg-gray-900 text-gray-100 rounded-lg p-2.5 overflow-x-auto whitespace-pre-wrap break-all">
        {text}
      </pre>
      <button
        onClick={() => onCopy(text, id)}
        className="absolute top-1.5 right-1.5 p-1 rounded bg-gray-700 text-gray-400 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
        title="Copy"
      >
        {copied === id ? <Check size={12} /> : <Copy size={12} />}
      </button>
    </div>
  );
}

function ParamTable({
  title,
  params,
  showRequired,
}: {
  title: string;
  params: any[];
  showRequired?: boolean;
}) {
  return (
    <div className="mb-2">
      <p className="text-[10px] font-medium text-gray-500 mb-1">{title}</p>
      <div className="overflow-x-auto rounded border border-gray-200 dark:border-gray-600">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700/50">
              <th className="text-left px-2 py-1 text-gray-500 font-medium">Name</th>
              <th className="text-left px-2 py-1 text-gray-500 font-medium">Type</th>
              {showRequired && <th className="text-left px-2 py-1 text-gray-500 font-medium">Req</th>}
              <th className="text-left px-2 py-1 text-gray-500 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {params.map((p: any, i: number) => (
              <tr key={i} className="border-t border-gray-100 dark:border-gray-600">
                <td className="px-2 py-1 font-mono text-gray-800 dark:text-gray-200">{p.name}</td>
                <td className="px-2 py-1 text-gray-500">{p.type}</td>
                {showRequired && (
                  <td className="px-2 py-1">
                    {p.required && <span className="text-red-500">*</span>}
                  </td>
                )}
                <td className="px-2 py-1 text-gray-500">{p.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
